/**
 * The reads the Overview widgets share. One definition per read, so two widgets
 * that need the same rows share one cache entry and one request, and cannot
 * disagree about how the payload is unwrapped.
 *
 * Every read carries LOAD_FAILURE_QUERY_OPTIONS and every widget reads its
 * failure with `queryLoadFailure`, so a dead API is said out loud instead of
 * collapsing into the empty array a quiet day also produces.
 *
 * The jobs and ticket reads take `enabled`: they are casing reads, and a
 * caller that knows the tenant has no casing switches them off rather than
 * asking a route that will refuse it (ActionQueueWidget).
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../../lib/load-failure';
import { dispatchJobsQueryKey, fetchDispatchJobs, type DispatchJobsCache } from '../../hooks/dispatchJobsQuery';
import type { TicketPipeline } from '../../types/dashboard-layout';
import type { ActionQueue } from '../../types/action-queue';
import type { EquipmentStatusRead, PipelineRead } from '../../types/overview-ops';
import type { PtJobs, ToolsInHole, WellsOnLocation, WlJobs, WorkOrders, WsJobs, WtJobs } from '../../types/overview-operations';
import type { ActivityFeed } from '../../types/overview-activity';
import type { OverviewBandRead } from '../../types/overview-band';
import type { ApprovedVsInvoiced, Receivables, RevenuePulse, UnbilledWork } from '../../types/overview-money';

const LIVE = { refetchInterval: 30_000, ...LOAD_FAILURE_QUERY_OPTIONS } as const;

/**
 * The unfiltered board's read, and the SAME cache entry Dispatch fills when
 * no filter is set — so it must use Dispatch's queryFn and store Dispatch's
 * shape. Its own queryFn here once stored the bare array; Dispatch → Overview
 * then read `{ jobs, tenantToday }` as a list and crashed the app. The array
 * this hook returns is derived by `select`, which never touches the cache.
 */
const NO_FILTERS: Record<string, string> = {};
export const useOverviewJobs = (enabled = true) =>
  useQuery({
    queryKey: dispatchJobsQueryKey(NO_FILTERS),
    enabled,
    queryFn: () => fetchDispatchJobs(NO_FILTERS),
    select: (d: DispatchJobsCache) => d.jobs as any[],
    ...LIVE,
  });

export const useOverviewTickets = (enabled = true) =>
  useQuery({
    queryKey: ['home-tickets'],
    enabled,
    queryFn: () => api.tickets.list().then((r) => (r.data.data ?? []) as any[]),
    ...LIVE,
  });

export const useOverviewTicketDash = (enabled = true) =>
  useQuery({
    queryKey: ['home-ticket-dash'],
    enabled,
    queryFn: () => api.tickets.dashboard().then((r) => (r.data.data ?? r.data ?? {}) as any),
    ...LIVE,
  });

export const useOverviewEquipment = () =>
  useQuery({
    queryKey: ['home-equipment'],
    queryFn: () => api.equipment.list().then((r) => (r.data.data ?? r.data ?? []) as any[]),
    ...LIVE,
  });

export const useOverviewEquipCerts = () =>
  useQuery({
    queryKey: ['home-equip-certs'],
    queryFn: () => api.equipment.certSummary().then((r) => (r.data.data ?? r.data ?? []) as any[]),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

export const useOverviewTeamCertSummary = () =>
  useQuery({
    queryKey: ['home-team-cert-summary'],
    queryFn: () => api.safety.teamCertSummary().then((r) => (r.data.data ?? r.data ?? {}) as any),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

export const useOverviewSafetyDash = () =>
  useQuery({
    queryKey: ['home-safety'],
    queryFn: () => api.safety.dashboard().then((r) => (r.data.data ?? r.data ?? {}) as any),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

export const useOverviewInvoiceDash = (enabled = true) =>
  useQuery({
    queryKey: ['home-invoice-dash'],
    enabled,
    queryFn: () => api.invoices.dashboard().then((r) => (r.data.data ?? r.data ?? {}) as any),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

export const useOverviewQuoteDash = () =>
  useQuery({
    queryKey: ['home-quote-dash'],
    queryFn: () => api.quotes.dashboard().then((r) => (r.data.data ?? r.data ?? {}) as any),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

export const useOverviewTimeOff = () =>
  useQuery({
    queryKey: ['home-timeoff'],
    queryFn: () => api.reports.timeOffDashboard().then((r) => (r.data.data ?? r.data ?? {}) as any),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

/**
 * GET /dashboard/action-queue — the non-casing verticals' items. `unwrap`, so
 * a null payload stays null and is not read as an empty queue. `enabled` is
 * false for a casing-only tenant: the route has nothing for it, and the casing
 * Action Queue makes exactly the reads it made before.
 */
export const useActionQueue = (enabled = true) =>
  useQuery({
    queryKey: ['dashboard-action-queue'],
    queryFn: () => api.dashboard.actionQueue().then((r) => unwrap<ActionQueue>(r.data)),
    enabled,
    ...LIVE,
  });

/**
 * GET /dashboard/revenue-pulse and /dashboard/receivables (api#1121) — every
 * vertical's money. `unwrap`, so a null payload stays null rather than reading
 * as an empty ledger. Asked only by a company with a vertical other than
 * casing; casing alone keeps its own reads (RevenuePulseWidget,
 * ReceivablesWidget).
 */
export const useRevenuePulse = (period: 'week' | 'month', enabled = true) =>
  useQuery({
    queryKey: ['dashboard-revenue-pulse', period],
    queryFn: () => api.dashboard.revenuePulse(period).then((r) => unwrap<RevenuePulse>(r.data)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

export const useReceivables = (enabled = true) =>
  useQuery({
    queryKey: ['dashboard-receivables'],
    queryFn: () => api.dashboard.receivables().then((r) => unwrap<Receivables>(r.data)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

/** GET /dashboard/unbilled-work and /dashboard/approved-vs-invoiced (api#1125), every vertical. */
export const useUnbilledWork = () =>
  useQuery({
    queryKey: ['dashboard-unbilled-work'],
    queryFn: () => api.dashboard.unbilledWork().then((r) => unwrap<UnbilledWork>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

export const useApprovedVsInvoiced = (period: 'week' | 'month') =>
  useQuery({
    queryKey: ['dashboard-approved-vs-invoiced', period],
    queryFn: () => api.dashboard.approvedVsInvoiced(period).then((r) => unwrap<ApprovedVsInvoiced>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

/**
 * GET /dashboard/pipeline and /dashboard/equipment-status (api#1136): every
 * vertical's tickets and units. Asked only by a company with a vertical other
 * than casing — casing alone keeps /dashboard/ticket-pipeline and /equipment,
 * which the casing goldens pin. `unwrap`, so a null payload stays null.
 */
export const usePipeline = (enabled = true) =>
  useQuery({
    queryKey: ['dashboard-pipeline'],
    queryFn: () => api.dashboard.pipeline().then((r) => unwrap<PipelineRead>(r.data)),
    enabled,
    ...LIVE,
  });

export const useEquipmentStatus = (enabled = true) =>
  useQuery({
    queryKey: ['dashboard-equipment-status'],
    queryFn: () => api.dashboard.equipmentStatus().then((r) => unwrap<EquipmentStatusRead>(r.data)),
    enabled,
    ...LIVE,
  });

/**
 * The vertical-operation widgets (api#1141). The catalogue decides who draws
 * them, so each read is asked only by its widget. `unwrap`, so a null payload
 * stays null and is never read as "nothing to show".
 */
/**
 * The API gates these three by vertical (requireVertical: downhole_tools,
 * pressure_testing) and answers a tenant without it 403. The widget passes
 * `enabled = hasVertical(...)` so that 403 is never fetched and read as a
 * permission refusal.
 */
export const useToolsInHole = (enabled = true) =>
  useQuery({ queryKey: ['dashboard-tools-in-hole'], queryFn: () => api.dashboard.toolsInHole().then((r) => unwrap<ToolsInHole>(r.data)), enabled, ...LIVE });
export const useWellsOnLocation = (enabled = true) =>
  useQuery({ queryKey: ['dashboard-wells-on-location'], queryFn: () => api.dashboard.wellsOnLocation().then((r) => unwrap<WellsOnLocation>(r.data)), enabled, ...LIVE });
export const usePtJobs = (enabled = true) =>
  useQuery({ queryKey: ['dashboard-pt-jobs'], queryFn: () => api.dashboard.ptJobs().then((r) => unwrap<PtJobs>(r.data)), enabled, ...LIVE });
export const useWtJobsWidget = (enabled = true) =>
  useQuery({ queryKey: ['dashboard-wt-jobs'], queryFn: () => api.dashboard.wtJobs().then((r) => unwrap<WtJobs>(r.data)), enabled, ...LIVE });
export const useWsJobsWidget = (enabled = true) =>
  useQuery({ queryKey: ['dashboard-ws-jobs'], queryFn: () => api.dashboard.wsJobs().then((r) => unwrap<WsJobs>(r.data)), enabled, ...LIVE });
export const useWlJobsWidget = (enabled = true) =>
  useQuery({ queryKey: ['dashboard-wl-jobs'], queryFn: () => api.dashboard.wlJobs().then((r) => unwrap<WlJobs>(r.data)), enabled, ...LIVE });
/**
 * GET /dashboard/band. `unwrap`: a null payload is not three zeros. Asked only
 * by a band that draws a strip (`enabled`).
 */
export const useOverviewBand = (enabled = true) =>
  useQuery({ queryKey: ['dashboard-band'], queryFn: () => api.dashboard.band().then((r) => unwrap<OverviewBandRead>(r.data)), enabled, ...LIVE });
export const useWorkOrders = () =>
  useQuery({ queryKey: ['dashboard-work-orders'], queryFn: () => api.dashboard.workOrders().then((r) => unwrap<WorkOrders>(r.data)), ...LIVE });
export const useActivityFeed = () =>
  useQuery({ queryKey: ['dashboard-activity-feed'], queryFn: () => api.dashboard.activityFeed().then((r) => unwrap<ActivityFeed>(r.data)), ...LIVE });

/**
 * GET /dashboard/ticket-pipeline. `unwrap`, not `?? {}`: a null payload is not
 * a pipeline of zeros, and the widgets branch on `data === null` for it.
 */
export const useTicketPipeline = (enabled = true) =>
  useQuery({
    queryKey: ['dashboard-ticket-pipeline'],
    queryFn: () => api.dashboard.ticketPipeline().then((r) => unwrap<TicketPipeline>(r.data)),
    enabled,
    ...LIVE,
  });
